import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import subscriptionApiController from "../../services/subscription.service";
import { normalizeCatalogPage, normalizeSelectedService } from "../../services/catalogResponse";
import {
  normalizeSubscriptionDetailResponse,
  normalizeSubscriptionInvoiceResponse,
} from "../../services/subscriptionResponse";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { contentErrorMessage } from "./contentError";
import { unwrapThunkResult } from "../unwrapThunkResult";
import { subscriptionCatalogRequest, subscriptionCatalogKey } from "../../services/subscriptionCatalogRequest";
export const usersAdapter = createEntityAdapter();
const listErrorMessage = (action, fallback) => contentErrorMessage(action, fallback);

const responseSucceeded = (response) => response && response.status >= 200 && response.status < 300;

export const SubscriptionStoreApi = createAsyncThunk("subscription/create", async (formvalues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.create(formvalues, thunkAPI);
    return HandleResponse(thunkAPI, response, "create");
  } catch (error) {
    return HandleError(thunkAPI, error, "create");
  }
});

export const SubscriptionUpdateApi = createAsyncThunk("subscription/update", async (formvalues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.update(formvalues, thunkAPI);
    const handled = HandleResponse(thunkAPI, response, "update");
    if (!responseSucceeded(response)) return handled;
    const payload = unwrapThunkResult(handled);
    return payload && typeof payload === "object" ? { ...payload, id: payload.id ?? formvalues?.id } : { id: formvalues?.id };
  } catch (error) {
    return HandleError(thunkAPI, error, "update");
  }
});

export const SubscriptionGridViewApi = createAsyncThunk("subscription/gridview", async (formValues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.view(formValues, thunkAPI);
    const handled = HandleResponse(thunkAPI, response, "gridview");
    if (!responseSucceeded(response)) return handled;
    return normalizeCatalogPage(unwrapThunkResult(handled));
  } catch (error) {
    return HandleError(thunkAPI, error, "gridview");
  }
});

export const SubscriptionOptions = createAsyncThunk("subscription/SubscriptionOptions", async (formValues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.view(formValues, thunkAPI);
    return HandleResponse(thunkAPI, response, "SubscriptionOptions");
  } catch (error) {
    return HandleError(thunkAPI, error, "SubscriptionOptions");
  }
});

export const SubscriptionDetailApi = createAsyncThunk("subscription/detail", async (formValues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.view(formValues, thunkAPI);
    const handled = HandleResponse(thunkAPI, response, "detail");
    if (!responseSucceeded(response)) return handled;
    const payload = unwrapThunkResult(handled);
    const detail = payload && payload.data && !Array.isArray(payload.data) && payload.data.id != null ? payload.data : payload;
    return normalizeSubscriptionDetailResponse(detail);
  } catch (error) {
    return HandleError(thunkAPI, error, "detail");
  }
});

export const SubscriptionDeleteApi = createAsyncThunk("subscription/delete", async (formValues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.deleted(formValues, thunkAPI);
    const handled = HandleResponse(thunkAPI, response, "delete");
    if (!responseSucceeded(response)) return handled;
    const payload = unwrapThunkResult(handled);
    return payload && typeof payload === "object" ? { ...payload, id: payload.id ?? formValues?.id } : { id: formValues?.id };
  } catch (error) {
    return HandleError(thunkAPI, error, "delete");
  }
});

const catalogSessionKey = (state) => {
  const auth = state.auth;
  return JSON.stringify([auth?.user?.salon_id, auth?.user?.auth_key, auth?.token]);
};

export const SubscriptionServiceApi = createAsyncThunk("subscription/services", async (formValues, thunkAPI) => {
  const sessionKey = () => catalogSessionKey(thunkAPI.getState());
  const startedSession = sessionKey();
  try {
    const request = subscriptionCatalogRequest(formValues);
    const response = await subscriptionApiController.services(request, thunkAPI);
    if (sessionKey() !== startedSession) return thunkAPI.rejectWithValue({staleCatalog: true});
    return HandleResponse(thunkAPI, response, "services");
  } catch (error) {
    if (sessionKey() !== startedSession) return thunkAPI.rejectWithValue({staleCatalog: true});
    return HandleError(thunkAPI, error, "services");
  }
}, {
  getPendingMeta: (_args, {getState}) => ({catalogSession: catalogSessionKey(getState())}),
  condition: (values, {getState}) => {
    try {
      const request = subscriptionCatalogRequest(values);
      const state = getState().subscription;
      if (state?.servicesSessionKey !== catalogSessionKey(getState())) return true;
      if (request.page > 1 && state?.servicesQueryKey === subscriptionCatalogKey(request) && request.page <= Number(state.isServices?.current_page || 0)) return false;
      return !(state?.isApiStatus?.SubscriptionServiceApi === "loading"
        && state.servicesQueryKey === subscriptionCatalogKey(request)
        && state.servicesRequest?.page === request.page);
    } catch (_) { return true; }
  },
});

export const SubscriptionServiceCartApi = createAsyncThunk("subscription/subscriptionservicecart", async (formValues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.services(formValues, thunkAPI);
    const handled = HandleResponse(thunkAPI, response, "subscriptionservicecart");
    if (!responseSucceeded(response)) return handled;
    const selectedService = normalizeSelectedService(unwrapThunkResult(handled), formValues && formValues.service_id);
    return selectedService || thunkAPI.rejectWithValue({ message: "Selected service was not returned." });
  } catch (error) {
    return HandleError(thunkAPI, error, "subscriptionservicecart");
  }
});

export const SubscriptionSuggetionListApi = createAsyncThunk("subscription/suggetionlist", async (formValues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.suggetionlist(formValues, thunkAPI);
    return HandleResponse(thunkAPI, response, "suggetionlist");
  } catch (error) {
    return HandleError(thunkAPI, error, "suggetionlist");
  }
});

export const SubscriptionInvoiceApi = createAsyncThunk("subscription/invoice/detail", async (formValues, thunkAPI) => {
  try {
    const response = await subscriptionApiController.invoice(formValues, thunkAPI);
    const handled = HandleResponse(thunkAPI, response, "suggetionlist");
    if (!responseSucceeded(response)) return handled;
    return normalizeSubscriptionInvoiceResponse(unwrapThunkResult(handled));
  } catch (error) {
    return HandleError(thunkAPI, error, "suggetionlist");
  }
});

const initialState = {
  isOpenedAddForm: "",
  isOpenedEditForm: "",
  isOpenedDetailModal: "",
  isGridView: [],
  isGridViewError: "",
  isSuggetionListView: [],
  isSuggetionListError: "",
  isDetailData: "",
  isSearchList: "",
  isSearchName: "",
  isSearchObj: "",
  isSubscriptionOption: [],
  isServices: [],
  isServicesError: "",
  servicesRequestId: null,
  servicesSessionKey: null,
  servicesQueryKey: null,
  servicesRequest: null,
  isSubscriptionServices: [],
  isSubscriptionInvoice: '',
  subscriptionInvoiceRequestId: null,
  isApiStatus: {
    SubscriptionStoreApi: "",
    SubscriptionUpdateApi: "",
    SubscriptionGridViewApi: "",
    SubscriptionDetailApi: "",
    SubscriptionDeleteApi: "",
    SubscriptionOptions: "",
    SubscriptionServiceApi: "",
    SubscriptionServiceCartApi: "",
    SubscriptionSuggetionListApi: "",
    SubscriptionInvoiceApi: '',
  },
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    reset: () => initialState,
    OpenAddSubscriptionForm: (state = initialState) => {
      state.isOpenedEditForm = "";
      state.isOpenedAddForm = "open";
    },
    CloseAddSubscriptionForm: (state = initialState) => {
      state.isOpenedEditForm = "";
      state.isOpenedAddForm = "";
    },
    OpenEditSubscriptionForm: (state = initialState) => {
      state.isOpenedAddForm = "";
      state.isOpenedEditForm = "open";
    },
    CloseEditSubscriptionForm: (state = initialState) => {
      state.isOpenedAddForm = "";
      state.isOpenedEditForm = "";
    },
    OpenSubscriptionDetailModal: (state = initialState) => {
      state.isOpenedAddForm = "";
      state.isOpenedDetailModal = "open";
    },
    CloseSubscriptionDetailModal: (state = initialState) => {
      state.isOpenedAddForm = "";
      state.isOpenedDetailModal = "";
    },
    SubscriptionServiceRemoveToCart: (state, action) => {
      const { id } = action.payload;
      state.isSubscriptionServices = state.isSubscriptionServices ? state.isSubscriptionServices.filter((item) => item.id != id) : [];
    },
    EditSubscriptionServiceCartApi: (state, action) => {
      state.isSubscriptionServices = action.payload;
    },
    OpenSubscriptionSearchList: (state) => {
      state.isSearchList = "open";
    },
    CloseSubscriptionSearchList: (state) => {
      state.isSearchList = "";
    },
    SubscriptionSearchName: (state, action) => {
      state.isSearchName = action.payload;
    },
    SubscriptionSearchObj: (state, action) => {
      state.isSearchObj = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(SubscriptionInvoiceApi.pending, (state, action) => {
        state.subscriptionInvoiceRequestId = action.meta.requestId;
        state.isSubscriptionInvoice = '';
        state.isApiStatus = { ...state.isApiStatus, SubscriptionInvoiceApi: "loading" };
      })
      .addCase(SubscriptionInvoiceApi.fulfilled, (state, action) => {
        if (state.subscriptionInvoiceRequestId !== action.meta.requestId) return;
        state.isSubscriptionInvoice = action.payload;
        state.isApiStatus = { ...state.isApiStatus, SubscriptionInvoiceApi: "succeeded" };
      })
      .addCase(SubscriptionInvoiceApi.rejected, (state, action) => {
        if (state.subscriptionInvoiceRequestId !== action.meta.requestId) return;
        state.isSubscriptionInvoice = '';
        state.isApiStatus = { ...state.isApiStatus, SubscriptionInvoiceApi: "failed" };
      })
      .addCase(SubscriptionStoreApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionStoreApi: "loading" };
      })
      .addCase(SubscriptionStoreApi.fulfilled, (state, action) => {
        if (state.isGridView && state.isGridView.data) {
          state.isGridView.data = [action.payload, ...state.isGridView.data];
        } else {
          state.isGridView = { data: [action.payload] };
        }
        state.isApiStatus = { ...state.isApiStatus, SubscriptionStoreApi: "succeeded" };
      })
      .addCase(SubscriptionStoreApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionStoreApi: "failed" };
      })
      .addCase(SubscriptionUpdateApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionUpdateApi: "loading" };
      })
      .addCase(SubscriptionUpdateApi.fulfilled, (state, action) => {
        const id = action.payload?.id ?? action.meta?.arg?.id;
        const changes = action.payload && typeof action.payload === "object" ? { ...action.payload } : {};
        delete changes.id;
        const rows = Array.isArray(state.isGridView?.data) ? state.isGridView.data : [];
        const existingData = rows.find((event) => String(event.id) === String(id));
        if (existingData) {
          Object.keys(changes).map((keyName) => {
            existingData[keyName] = changes[keyName];
          });
        }
        state.isApiStatus = { ...state.isApiStatus, SubscriptionUpdateApi: "succeeded" };
      })
      .addCase(SubscriptionUpdateApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionUpdateApi: "failed" };
      })
      .addCase(SubscriptionGridViewApi.pending, (state) => {
        state.isGridViewError = "";
        state.isApiStatus = { ...state.isApiStatus, SubscriptionGridViewApi: "loading" };
      })
      .addCase(SubscriptionGridViewApi.fulfilled, (state, action) => {
        let old_current_page = state.isGridView.current_page ? state.isGridView.current_page : "";
        let new_current_page = action.payload.current_page ? action.payload.current_page : "";
        let viewdata = state.isGridView && state.isGridView.data;
        let newviewdata = action.payload && action.payload.data;
        if (old_current_page && new_current_page && old_current_page < new_current_page && old_current_page != new_current_page) {
          state.isGridView = viewdata && newviewdata
            ? { ...action.payload, data: [...viewdata, ...newviewdata] }
            : action.payload;
        } else {
          state.isGridView = normalizeCatalogPage(action.payload);
        }
        state.isApiStatus = { ...state.isApiStatus, SubscriptionGridViewApi: "succeeded" };
        state.isGridViewError = "";
      })
      .addCase(SubscriptionGridViewApi.rejected, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionGridViewApi: "failed" };
        state.isGridViewError = listErrorMessage(action, "Unable to load subscriptions.");
      })
      .addCase(SubscriptionDetailApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionDetailApi: "loading" };
      })
      .addCase(SubscriptionDetailApi.fulfilled, (state, action) => {
        state.isDetailData = action.payload;
        state.isApiStatus = { ...state.isApiStatus, SubscriptionDetailApi: "succeeded" };
      })
      .addCase(SubscriptionDetailApi.rejected, (state) => {
        state.isDetailData = "";
        state.isApiStatus = { ...state.isApiStatus, SubscriptionDetailApi: "failed" };
      })
      .addCase(SubscriptionDeleteApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionDeleteApi: "loading" };
      })
      .addCase(SubscriptionDeleteApi.fulfilled, (state, action) => {
        const id = action.payload?.id ?? action.meta?.arg?.id;
        state.isApiStatus = { ...state.isApiStatus, SubscriptionDeleteApi: "succeeded" };
        if (Array.isArray(state.isGridView?.data)) {
          state.isGridView.data = state.isGridView.data.filter((item) => String(item.id) !== String(id));
        } else if (Array.isArray(state.isGridView)) {
          state.isGridView = state.isGridView.filter((item) => String(item.id) !== String(id));
        }
      })
      .addCase(SubscriptionDeleteApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionDeleteApi: "failed" };
      })
      .addCase(SubscriptionOptions.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionOptions: "loading" };
      })
      .addCase(SubscriptionOptions.fulfilled, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionOptions: "succeeded" };
        state.isSubscriptionOption = action.payload;
      })
      .addCase(SubscriptionOptions.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionOptions: "failed" };
        state.isSubscriptionOption = [];
      })
      .addCase(SubscriptionServiceApi.pending, (state, action) => {
        let request;
        try { request = subscriptionCatalogRequest(action.meta.arg); } catch (_) { request = null; }
        const key = request ? subscriptionCatalogKey(request) : null;
        if ((state.servicesQueryKey !== null && state.servicesQueryKey !== key)
          || state.servicesSessionKey !== (action.meta.catalogSession ?? null)) state.isServices = [];
        state.servicesSessionKey = action.meta.catalogSession ?? null;
        state.servicesRequestId = action.meta.requestId;
        state.servicesQueryKey = key;
        state.servicesRequest = request;
        state.isApiStatus.SubscriptionServiceApi = "loading";
        state.isServicesError = "";
      })
      .addCase(SubscriptionServiceApi.fulfilled, (state, action) => {
        if (state.servicesRequestId && state.servicesRequestId !== action.meta?.requestId) return;
        const incoming = normalizeCatalogPage(action.payload);
        const page = Number(incoming?.current_page || 1);
        const current = Number(state.isServices?.current_page || 0);
        if (page > 1 && page !== current + 1) {
          if (page > current) {
            state.isApiStatus.SubscriptionServiceApi = "failed";
            state.isServicesError = "The next services page could not be loaded. Please retry.";
          }
          return;
        }
        if (page > 1) {
          const seen = new Set();
          const rows = [...(state.isServices?.data || []), ...(incoming?.data || [])]
            .filter(row => { const id = String(row.id); if (seen.has(id)) return false; seen.add(id); return true; });
          state.isServices = {...incoming, data: rows};
        } else {
          state.isServices = action.payload;
        }
        state.isApiStatus.SubscriptionServiceApi = "succeeded";
        state.isServicesError = "";
      })
      .addCase(SubscriptionServiceApi.rejected, (state, action) => {
        if (action.meta?.condition || (state.servicesRequestId && state.servicesRequestId !== action.meta?.requestId)) return;
        if (action.payload?.staleCatalog) {
          state.isServices = [];
          state.isServicesError = "";
          state.isApiStatus.SubscriptionServiceApi = "";
          return;
        }
        state.isApiStatus.SubscriptionServiceApi = "failed";
        state.isServicesError = listErrorMessage(action, "Unable to load subscription services.");
      })
      .addCase(SubscriptionServiceCartApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionServiceCartApi: "loading" };
      })
      .addCase(SubscriptionServiceCartApi.fulfilled, (state, action) => {
        const selectedService = normalizeSelectedService(action.payload, action.meta && action.meta.arg && action.meta.arg.service_id);
        if (selectedService) {
          const existingIndex = state.isSubscriptionServices.findIndex(
            (event) => String(event.id) === String(selectedService.id),
          );
          if (existingIndex >= 0) {
            state.isSubscriptionServices[existingIndex] = { ...state.isSubscriptionServices[existingIndex], ...selectedService };
          } else {
            state.isSubscriptionServices.push(selectedService);
          }
        }
        state.isApiStatus = { ...state.isApiStatus, SubscriptionServiceCartApi: "succeeded" };
      })
      .addCase(SubscriptionServiceCartApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionServiceCartApi: "failed" };
      })
      .addCase(SubscriptionSuggetionListApi.pending, (state) => {
        state.isSuggetionListError = "";
        state.isApiStatus = { ...state.isApiStatus, SubscriptionSuggetionListApi: "loading" };
      })
      .addCase(SubscriptionSuggetionListApi.fulfilled, (state, action) => {
        let old_current_page = state.isSuggetionListView.current_page ? state.isSuggetionListView.current_page : "";
        let new_current_page = action.payload.current_page ? action.payload.current_page : "";
        let viewdata = state.isSuggetionListView && state.isSuggetionListView.data;
        let newviewdata = action.payload && action.payload.data;
        state.isSuggetionListView = action.payload;
        if (old_current_page && new_current_page && old_current_page < new_current_page && old_current_page != new_current_page) {
          viewdata && newviewdata ? (state.isSuggetionListView.data = [...viewdata, ...newviewdata]) : action.payload;
        }
        state.isApiStatus = { ...state.isApiStatus, SubscriptionSuggetionListApi: "succeeded" };
        state.isSuggetionListView = action.payload;
        state.isSuggetionListError = "";
      })
      .addCase(SubscriptionSuggetionListApi.rejected, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, SubscriptionSuggetionListApi: "failed" };
        state.isSuggetionListError = listErrorMessage(action, "Unable to search subscriptions.");
      });
  }
});
// Action creators are generated for each case reducer function
export const { reset, OpenAddSubscriptionForm, CloseAddSubscriptionForm, OpenEditSubscriptionForm, CloseEditSubscriptionForm, OpenSubscriptionDetailModal, CloseSubscriptionDetailModal, openSubscriptionSearchList, closesubscriptionsearchList, subscriptionSearchName, SubscriptionServiceRemoveToCart, EditSubscriptionServiceCartApi, OpenSubscriptionSearchList, CloseSubscriptionSearchList, SubscriptionSearchName, SubscriptionSearchObj } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
